import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  UserPlus, MessageCircle, CreditCard, FlaskConical, Trophy,
  BookOpen, Bookmark, Clock, X, Globe, Shield, AlertTriangle,
  Activity, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

const ICON_MAP = {
  'user-plus': UserPlus,
  'message-circle': MessageCircle,
  'credit-card': CreditCard,
  'flask-conical': FlaskConical,
  'trophy': Trophy,
  'book-open': BookOpen,
  'bookmark': Bookmark,
  'clock': Clock,
};

const TYPE_COLORS = {
  signup: '#22c55e',
  first_message: '#6366f1',
  subscription: '#f59e0b',
  trial: '#f97316',
  milestone: '#ec4899',
  conversation: '#8b5cf6',
  bookmark: '#14b8a6',
  last_active: '#64748b',
};

const RISK_CONFIG = {
  high: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'High Risk', desc: 'Was active (10+ msgs) but silent 14+ days' },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'At Risk', desc: 'Was active (10+ msgs) but silent 7+ days' },
  low: { color: '#eab308', bg: 'rgba(234,179,8,0.12)', label: 'Watch', desc: 'Engaged (3+ msgs) but silent 7+ days' },
  none: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'Healthy', desc: 'Recently active or new user' },
};

function countryFlag(code) {
  if (!code) return '';
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt()));
}

export default function UserJourneyDrawer({ user, open, onClose }) {
  const { isDark, colors } = useAdminTheme();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.user_id) return;
    setLoading(true);
    setJourney(null);
    Promise.all([
      axios.get(`${API}/admin/users/${user.user_id}`, { withCredentials: true }),
      axios.get(`${API}/admin/users/${user.user_id}/journey`, { withCredentials: true }),
    ]).then(([detailRes, journeyRes]) => {
      setJourney({ detail: detailRes.data, ...journeyRes.data });
    }).catch(() => {
      setJourney({ detail: user, events: [], stats: {}, retention: { risk: 'none' } });
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.user_id]);

  const detail = journey?.detail || user || {};
  const events = journey?.events || [];
  const stats = journey?.stats || {};
  const retention = journey?.retention || { risk: 'none' };
  const tier = detail.subscription_tier || 'free';
  const tierColors = { free: '#64748b', paid: '#6366f1', premium: '#8b5cf6', share: '#10b981' };
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] overflow-y-auto p-0 border-l"
        style={{
          background: isDark ? '#0f172a' : '#fff',
          borderColor: colors.cardBorder,
        }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>User Details</SheetTitle>
        </SheetHeader>

        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-5" style={{
          background: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}>
          <button onClick={() => onClose(false)} className="absolute right-4 top-4 rounded-lg p-1.5 transition" style={{ color: colors.textMuted, ':hover': { background: hoverBg } }} data-testid="close-journey-drawer"
            onMouseEnter={(e) => e.currentTarget.style.background = hoverBg}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold shrink-0" style={{
              background: `${tierColors[tier]}20`,
              color: tierColors[tier],
            }}>
              {detail.name ? detail.name[0].toUpperCase() : '?'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-semibold truncate" style={{ color: colors.text }} data-testid="journey-user-name">
                  {detail.name || 'Unnamed'}
                </h2>
                {detail.country_code && <span className="text-base leading-none">{countryFlag(detail.country_code)}</span>}
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase" style={{ background: `${tierColors[tier]}20`, color: tierColors[tier] }}>
                  {tier}
                </span>
              </div>
              <p className="text-xs truncate mt-0.5" style={{ color: colors.textMuted }}>{detail.email}</p>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {detail.auth_method && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium" style={{
                    background: detail.auth_method === 'google' ? 'rgba(234,179,8,0.12)' : 'rgba(99,102,241,0.12)',
                    color: detail.auth_method === 'google' ? '#eab308' : '#818cf8',
                  }}>{detail.auth_method === 'google' ? 'Google' : 'Email'}</span>
                )}
                {detail.country && (
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                    <Globe className="h-3 w-3 inline mr-0.5" />{detail.country}
                  </span>
                )}
                {detail.share_role && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium" style={{
                    background: detail.share_role === 'owner' ? 'rgba(16,185,129,0.15)' : 'rgba(56,189,248,0.15)',
                    color: detail.share_role === 'owner' ? '#34d399' : '#38bdf8',
                  }}>{detail.share_role}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-6 w-6 border-2 border-indigo-400 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {/* Retention Risk Banner */}
            {retention.risk !== 'none' && (
              <RetentionBanner retention={retention} colors={colors} isDark={isDark} />
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-3" data-testid="journey-stats">
              {[
                { label: 'Messages', value: stats.total_messages ?? detail.message_count ?? 0, icon: MessageSquare, color: '#8b5cf6' },
                { label: 'Convos', value: stats.total_conversations ?? detail.conversation_count ?? 0, icon: BookOpen, color: '#6366f1' },
                { label: 'Bookmarks', value: stats.total_bookmarks ?? 0, icon: Bookmark, color: '#14b8a6' },
                { label: 'Days', value: stats.days_active ?? 0, icon: Activity, color: '#ec4899' },
              ].map((s) => (
                <div key={s.label} className="rounded-xl p-3 text-center" style={{
                  background: isDark ? 'rgba(30,41,59,0.5)' : '#f8fafc',
                  border: `1px solid ${colors.cardBorder}`,
                }} data-testid={`stat-${s.label.toLowerCase()}`}>
                  <s.icon className="h-3.5 w-3.5 mx-auto mb-1.5" style={{ color: s.color }} />
                  <p className="text-lg font-bold" style={{ color: colors.text }}>{s.value}</p>
                  <p className="text-[10px] font-medium" style={{ color: colors.textMuted }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Retention Risk (healthy) - subtle indicator */}
            {retention.risk === 'none' && stats.total_messages > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{
                background: RISK_CONFIG.none.bg,
                border: `1px solid rgba(34,197,94,0.2)`,
              }} data-testid="retention-healthy">
                <div className="w-2 h-2 rounded-full" style={{ background: RISK_CONFIG.none.color }} />
                <span style={{ color: RISK_CONFIG.none.color }} className="font-medium">{RISK_CONFIG.none.label}</span>
                <span style={{ color: colors.textMuted }}>
                  {retention.days_since_last_message != null
                    ? `Active ${retention.days_since_last_message === 0 ? 'today' : `${retention.days_since_last_message}d ago`}`
                    : 'Recently active'}
                </span>
              </div>
            )}

            {/* Subscription Details */}
            {detail.subscription_details && (
              <div className="rounded-xl p-4" style={{
                background: isDark ? 'rgba(30,41,59,0.4)' : '#f8fafc',
                border: `1px solid ${colors.cardBorder}`,
              }} data-testid="journey-subscription">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textMuted }}>
                  <CreditCard className="h-3.5 w-3.5 inline mr-1.5" />Subscription
                </h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span style={{ color: colors.textMuted }}>Status</span>
                    <p className="font-medium mt-0.5" style={{ color: detail.subscription_details.status === 'active' ? '#22c55e' : '#f59e0b' }}>
                      {detail.subscription_details.status || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted }}>Tier</span>
                    <p className="font-medium mt-0.5" style={{ color: colors.text }}>{detail.subscription_details.tier || 'free'}</p>
                  </div>
                  {detail.subscription_details.is_trial && (
                    <div>
                      <span style={{ color: colors.textMuted }}>Trial</span>
                      <p className="font-medium mt-0.5" style={{ color: '#f97316' }}>Active</p>
                    </div>
                  )}
                  {detail.subscription_details.stripe_subscription_id && (
                    <div className="col-span-2">
                      <span style={{ color: colors.textMuted }}>Stripe ID</span>
                      <p className="font-mono text-[11px] mt-0.5 truncate" style={{ color: colors.textSecondary }}>{detail.subscription_details.stripe_subscription_id}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div data-testid="journey-timeline">
              <h3 className="text-xs font-semibold uppercase tracking-wide mb-4 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
                <Clock className="h-3.5 w-3.5" />User Journey
                <span className="ml-auto text-[10px] font-normal">{events.length} events</span>
              </h3>

              {events.length === 0 ? (
                <p className="text-xs text-center py-8" style={{ color: colors.textMuted }}>No journey events found</p>
              ) : (
                <div className="relative pl-6">
                  {/* Vertical line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-px" style={{ background: isDark ? '#1e293b' : '#e2e8f0' }} />

                  {events.map((ev, i) => {
                    const IconComp = ICON_MAP[ev.icon] || Clock;
                    const color = TYPE_COLORS[ev.type] || '#64748b';
                    const date = new Date(ev.timestamp);
                    const isLast = i === events.length - 1;

                    return (
                      <div key={ev.timestamp + ev.type + i} className={`relative flex gap-3 ${isLast ? '' : 'pb-5'}`} data-testid={`journey-event-${i}`}>
                        {/* Dot */}
                        <div className="absolute -left-6 w-[22px] h-[22px] rounded-full flex items-center justify-center z-10 shrink-0" style={{
                          background: `${color}20`,
                          border: `2px solid ${color}`,
                        }}>
                          <IconComp className="h-2.5 w-2.5" style={{ color }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold" style={{ color: colors.text }}>{ev.title}</span>
                            {ev.type === 'milestone' && <Trophy className="h-3 w-3 text-pink-400" />}
                          </div>
                          {ev.description && (
                            <p className="text-[11px] mt-0.5 truncate" style={{ color: colors.textMuted }}>{ev.description}</p>
                          )}
                          <p className="text-[10px] mt-1" style={{ color: colors.textMuted }}>
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {' '}
                            {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Devices */}
            {detail.devices && detail.devices.length > 0 && (
              <div data-testid="journey-devices">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textMuted }}>
                  <Shield className="h-3.5 w-3.5 inline mr-1.5" />Devices ({detail.devices.length})
                </h3>
                <div className="space-y-2">
                  {detail.devices.map((d, i) => (
                    <div key={d.device_id || d.platform + i} className="rounded-lg px-3 py-2 text-xs" style={{
                      background: isDark ? 'rgba(30,41,59,0.4)' : '#f8fafc',
                      border: `1px solid ${colors.cardBorder}`,
                    }}>
                      <span style={{ color: colors.text }}>{d.platform || d.device_type || 'Unknown'}</span>
                      {d.last_seen && <span className="ml-2" style={{ color: colors.textMuted }}>{new Date(d.last_seen).toLocaleDateString()}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function RetentionBanner({ retention, colors, isDark }) {
  const cfg = RISK_CONFIG[retention.risk] || RISK_CONFIG.none;
  return (
    <div className="rounded-xl p-4" style={{
      background: cfg.bg,
      border: `1px solid ${cfg.color}30`,
    }} data-testid="retention-risk-banner">
      <div className="flex items-center gap-2.5">
        <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: cfg.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-md font-semibold uppercase" style={{ background: `${cfg.color}20`, color: cfg.color }}>
              {retention.risk}
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: colors.textSecondary }}>{cfg.desc}</p>
          {retention.days_since_last_message != null && (
            <p className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>
              Last message: {retention.days_since_last_message} days ago · {retention.total_messages} total messages
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
